import { without, uniq, get, map } from 'lodash';
import { batchActions } from 'redux-batched-actions';
import { createAction } from 'redux-actions';
import Constants from 'taker/constants';
import ActionConstants from 'constants/actions';
import { submit } from 'redux-form';
import postAnswerList from 'taker/api/postAnswerList';
import getTakerSurvey from 'taker/api/getTakerSurvey';
import patchResponse from 'taker/api/patchResponse';
import postCaptchaResponseToken from 'taker/api/postCaptchaResponseToken';
import { showError } from 'taker/actions/errorActions';
import { handleTakerApiError } from 'taker/api/utils/handleTakerApiError';

import {
  getSurveyResponseUuid,
  getFirstPageNumber,
  getNextPageNumber,
  getRedirectUrl,
  isBlockNextButton,
  isBlockNextOrSubmitButton,
  getInvalidQuestionFormNames,
  getFirstInvalidFormName,
  getAwaitForAnswerNextButtonId,
  isAllowedToNextPage,
  isQuestionLogicValid,
  getQuestionLogicRedirectBlockId,
  getSurveyAnswerById,
  getPendingRedirectToBlockId,
  isAnyAnswerPending,
  getPendingRedirectIdByNextPageNumber,
  getBlockPage,
  getLatestVisitedPageNumber,
  getVisitedPages,
  getVisitedPagesLength,
  getVisitedQuestions,
  isSurveyNextPageRedirect,
  getCurrentPageBlockList,
  getRedirectToBlockId,
  getVisibleQuestionBlocks,
  getQuestionBlockList,
  getConditionalShowLogicQuestionVisibility,
  isQuestionLogicActionSkip,
} from 'taker/reducers';

import isPageLoadedInIframe from 'utils/surveyEmbedding/isPageLoadedInIframe';
import drawSpinnerInWindow from 'utils/surveyEmbedding/drawSpinnerInWindow';
import { getQuestionBlockIds } from 'utils/getQuestionBlockIds';
import getConditionalShowLogicQuestions from 'utils/getConditionalShowLogicQuestions';
import { setLocalStyling } from '../../domains/styling/actions/index';
import { applySectionHeaders } from '../../actions/headerEditorActions';

const defaultBtnAnswer = { value: true };

export const setQuestionNumbers = createAction(ActionConstants.SET_QUESTION_NUMBERS);

export const setVisitedQuestionNumbers = createAction(ActionConstants.SET_VISITED_QUESTION_NUMBERS);

export const resetSurveyData = createAction(Constants.RESET_CLIENT_SURVEY_DATA);

export const setSurveyBlockList = createAction(Constants.SET_CLIENT_SURVEY_BLOCK_LIST);

export const setSurveyConditionalShowLogicQuestions = createAction(
  Constants.SET_CLIENT_SURVEY_CONDITIONAL_SHOW_LOGIC_QUESTIONS,
);

export const setSectionHeaders = createAction(ActionConstants.SET_SECTION_HEADERS);

export const setSurveyInitialSettings = createAction(Constants.SET_CLIENT_SURVEY_SETTINGS);

export const updateSurveySettings = createAction(Constants.SET_CLIENT_SURVEY_SETTINGS);

export const toggleConditionalShowLogicQuestionVisiblity = createAction(
  Constants.TOGGLE_CONDITIONAL_SHOW_LOGIC_QUESTION_VISIBILITY,
);

export const setResponseUuid = createAction(Constants.SET_RESPONSE_UUID);

export const popFromVisitedPages = createAction(Constants.POP_FROM_VISITED_PAGES);

export const pushToVisitedPages = createAction(Constants.PUSH_TO_VISITED_PAGES);

export const pushToVisitedQuestions = createAction(Constants.SET_VISITED_QUESTIONS);

export const putToVisitedPages = (value) => (dispatch, getState) => {
  const latestVisitedPageNumber = getLatestVisitedPageNumber(getState());

  if (value !== latestVisitedPageNumber) {
    dispatch(pushToVisitedPages(value));
  }
};

export const putToVisitedQuestions = () => (dispatch, getState) => {
  const state = getState();
  const visitedQuestions = getVisitedQuestions(state);

  const currentPageQuestionIds = getQuestionBlockIds(
    getVisibleQuestionBlocks(state, getCurrentPageBlockList(state)),
  );

  const currentQuestionBlockList = getQuestionBlockList(state);
  const newVisitedQuestionsRaw = uniq([...visitedQuestions, ...currentPageQuestionIds]);
  const newVisitedQuestionsSorted = newVisitedQuestionsRaw.sort(
    (a, b) =>
      currentQuestionBlockList.findIndex((el) => el.id === a) -
      currentQuestionBlockList.findIndex((el) => el.id === b),
  );

  dispatch(pushToVisitedQuestions(newVisitedQuestionsSorted));
};

export const popFromVisitedQuestions = () => (dispatch, getState) => {
  const state = getState();
  const visitedQuestions = getVisitedQuestions(state);
  const lastPageBlockIds = getQuestionBlockIds(getCurrentPageBlockList(state));

  dispatch(pushToVisitedQuestions(without(visitedQuestions, ...lastPageBlockIds)));
};

export const setVisitedPages = createAction(Constants.SET_VISITED_PAGES);

export const setVisitedQuestions = createAction(Constants.SET_VISITED_QUESTIONS);

export const setCurrentPage = createAction(Constants.SET_CURRENT_PAGE);

export const setAnswer = createAction(Constants.SET_ANSWER, (id, answer) => ({
  id,
  answer,
}));

export const setAnswerList = createAction(Constants.SET_ANSWER_LIST);

export const setInvalidFormNameToScroll = createAction(Constants.SET_INVALID_FORM_NAME_TO_SCROLL);

export const clearInvalidFormNameToScroll = createAction(Constants.CLEAR_INVALID_FORM_NAME_TO_SCROLL);

export const removeBlockFromAnswerPendingQueue = createAction(
  Constants.REMOVE_BLOCK_FROM_ANSWER_PENDING_QUEUE,
);

export const addBlockToAnswerPendingQueue = createAction(Constants.ADD_BLOCK_TO_ANSWER_PENDING_QUEUE);

export const clearAwaitNextButtonId = createAction(Constants.CLEAR_AWAIT_NEXT_BUTTON_ID);

export const setAwaitForAnswerNextButtonId = createAction(Constants.SET_AWAIT_FOR_ANSWER_NEXT_BUTTON_ID);

export const setLogicRedirectToQuestionId = createAction(Constants.SET_LOGIC_REDIRECT_TO_QUESTION_ID);

export const setPendingLogicRedirectToQuestionId = createAction(
  Constants.SET_PENDING_LOGIC_REDIRECT_TO_QUESTION_ID,
);

export const setIsCaptchaVerified = createAction(Constants.SET_IS_CAPTCHA_VERIFIED);

export const setIsCaptchaPending = createAction(Constants.SET_IS_CAPTCHA_PENDING);

export const setIsSurveyCompleted = createAction(Constants.SET_IS_SURVEY_COMPLETED);

export const setSharedHeader = createAction(Constants.SET_SHARED_HEADER);

export const verifyCaptcha = (token) => (dispatch) => {
  dispatch(setIsCaptchaPending(true));
  return postCaptchaResponseToken(token)
    .catch(() => dispatch(setIsCaptchaVerified(false)))
    .then(({ success }) => dispatch(setIsCaptchaVerified(success)))
    .finally(() => dispatch(setIsCaptchaPending(false)));
};

export const goToRedirectUrl = (popupWindow = null) => (dispatch, getState) => {
  const redirectUrl = getRedirectUrl(getState());

  if (isPageLoadedInIframe() && popupWindow !== null) {
    popupWindow.location.replace(redirectUrl);
    return;
  }

  window.location.replace(redirectUrl);
};

export const handleLogicRedirectOnNextPage = (nextPage) => (dispatch, getState) => {
  const state = getState();
  const pendingRedirectId = getPendingRedirectIdByNextPageNumber(state, nextPage);

  if (pendingRedirectId) {
    dispatch(setPendingLogicRedirectToQuestionId(pendingRedirectId));
  }
};

export const persistVisitedPages = (dispatch, getState) => {
  const state = getState();
  const responseUuid = getSurveyResponseUuid(state);
  const visitedPages = getVisitedPages(state);
  const visitedQuestions = getVisitedQuestions(state);
  patchResponse(responseUuid, { visitedPages, visitedQuestions });
};

export const goToNextPage = () => (dispatch, getState) => {
  const state = getState();
  const nextPage = getNextPageNumber(state);

  dispatch(handleLogicRedirectOnNextPage(nextPage));
  dispatch(setCurrentPage(nextPage));

  dispatch(putToVisitedPages(nextPage));
  dispatch(putToVisitedQuestions());
  dispatch(persistVisitedPages);
  dispatch(setVisitedQuestionNumbers());
};

export const handleLogicRedirectOnPreviousPage = () => (dispatch, getState) => {
  const redirectToBlockId = getRedirectToBlockId(getState());

  if (redirectToBlockId) {
    dispatch(setPendingLogicRedirectToQuestionId(redirectToBlockId));
  }
};

export const goToPreviousPage = (dispatch, getState) => {
  dispatch(popFromVisitedQuestions());
  dispatch(popFromVisitedPages());
  dispatch(handleLogicRedirectOnPreviousPage());

  const latestVisitedPage = getLatestVisitedPageNumber(getState());
  dispatch(setCurrentPage(latestVisitedPage));
  dispatch(persistVisitedPages);
  dispatch(setVisitedQuestionNumbers());
};

export const submitInvalidQuestionForms = () => (dispatch, getState) => {
  // action creator submit used to kick up validation @sinslav
  getInvalidQuestionFormNames(getState()).forEach((name) => dispatch(submit(name)));
};

export const persistAnswer = (id, answer) => (dispatch, getState) => {
  const oldAnswer = getSurveyAnswerById(getState());
  dispatch(batchActions([addBlockToAnswerPendingQueue(id), setAnswer(id, answer)]));
  return postAnswerList({
    responseUuid: getSurveyResponseUuid(getState()),
    questionId: id,
    answer,
  })
    .catch((rejection) => {
      dispatch(batchActions([clearAwaitNextButtonId(), setAnswer(id, oldAnswer)]));
      handleTakerApiError(rejection, dispatch);
      return Promise.reject(rejection);
    })
    .finally(() => dispatch(removeBlockFromAnswerPendingQueue(id)));
};

export const handleLogicRedirectOnNext = () => (dispatch, getState) => {
  const state = getState();
  const pendingLogicRedirectToQuestionId = getPendingRedirectToBlockId(state);
  dispatch(
    batchActions([
      setLogicRedirectToQuestionId(pendingLogicRedirectToQuestionId),
      setPendingLogicRedirectToQuestionId(null),
    ]),
  );
};

export const handleNextSubmitPress = (id) => (dispatch, getState) => {
  const state = getState();
  if (isAnyAnswerPending(state)) {
    dispatch(setAwaitForAnswerNextButtonId(id));
  }

  if (isAllowedToNextPage(state)) {
    const isRedirect = isSurveyNextPageRedirect(state);
    const isIframe = isPageLoadedInIframe();
    const isRedirectFromIframe = isRedirect && isIframe;
    let popupWindow = null;

    if (isRedirectFromIframe) {
      popupWindow = window.open('', '_blank');
      drawSpinnerInWindow(popupWindow);
    }
    const answer = { ...defaultBtnAnswer };

    return dispatch(persistAnswer(id, answer))
      .then(() => {
        if (isBlockNextButton(state, id)) {
          dispatch(handleLogicRedirectOnNext());
        }

        if (isRedirectFromIframe) {
          dispatch(setIsSurveyCompleted(true));
        }

        if (isRedirect) {
          dispatch(goToRedirectUrl(popupWindow));
          return;
        }

        dispatch(goToNextPage());
      })
      .finally(() => {
        dispatch(clearAwaitNextButtonId());
      });
  }
  dispatch(submitInvalidQuestionForms());
  dispatch(setInvalidFormNameToScroll(getFirstInvalidFormName(getState())));
};

const handleLogicRedirectOnAnswer = (questionId, answer) => (dispatch, getState) => {
  const state = getState();
  if (isQuestionLogicValid(state, questionId) && isQuestionLogicActionSkip(state, questionId)) {
    const blockToJumpId = getQuestionLogicRedirectBlockId(state, questionId, answer);
    dispatch(setPendingLogicRedirectToQuestionId(blockToJumpId));
  }
};

export const answerQuestion = (id, answer) => (dispatch, getState) => {
  const state = getState();

  return isBlockNextOrSubmitButton(state, id)
    ? dispatch(handleNextSubmitPress(id))
    : dispatch(persistAnswer(id, answer)).then(() => {
        dispatch(handleLogicRedirectOnAnswer(id, answer));
        const waitingNextButtonId = getAwaitForAnswerNextButtonId(getState());
        if (waitingNextButtonId) {
          return dispatch(handleNextSubmitPress(waitingNextButtonId));
        }
      });
};

const setInitialRedirectBlockIds = (redirectedFromPage, redirectedToPage) => (dispatch, getState) => {
  const state = getState();
  const pendingRedirectId = getPendingRedirectIdByNextPageNumber(state, redirectedToPage);
  if (pendingRedirectId) {
    dispatch(setPendingLogicRedirectToQuestionId(pendingRedirectId));
  }
  const currentRedirectId = getPendingRedirectIdByNextPageNumber(state, redirectedFromPage);
  if (currentRedirectId) {
    dispatch(setLogicRedirectToQuestionId(currentRedirectId));
  }
};

const setInitialPage = (lastClickedNextButtonId) => (dispatch, getState) => {
  const state = getState();
  const currentPage = getVisitedPagesLength(state)
    ? getLatestVisitedPageNumber(state)
    : getFirstPageNumber(state);
  const prevPage = getBlockPage(state, lastClickedNextButtonId);
  dispatch(setInitialRedirectBlockIds(prevPage, currentPage));
  dispatch(setCurrentPage(currentPage));
  dispatch(putToVisitedPages(currentPage));
  dispatch(putToVisitedQuestions());
  dispatch(setVisitedQuestionNumbers());
};

export const updateConditionalShowLogicQuestionState = (questionUuid) => (dispatch, getState) => {
  const showLogicVisibility = getConditionalShowLogicQuestionVisibility(getState(), questionUuid);
  if (showLogicVisibility) {
    dispatch(popFromVisitedQuestions());
  }
  dispatch(toggleConditionalShowLogicQuestionVisiblity(questionUuid));
  dispatch(putToVisitedQuestions());
  dispatch(persistVisitedPages);
  dispatch(setVisitedQuestionNumbers());
};

export const initTakerApp = (uuid, caseParams) => (dispatch) =>
  getTakerSurvey(uuid, caseParams)
    .catch((rejection) => {
      dispatch(showError(rejection));
      return Promise.reject(rejection);
    })
    .then(
      ({
        surveyBlockList,
        responseUuid,
        settings: { headerUuid, ...settings },
        answerList,
        headers,
        localStyling,
        lastClickedNextButtonId,
        visitedPages,
        visitedQuestions,
      }) => {
        dispatch(
          batchActions([
            setSurveyInitialSettings(settings),
            setLocalStyling(localStyling),
            setSurveyBlockList(surveyBlockList),
            setSurveyConditionalShowLogicQuestions(
              getConditionalShowLogicQuestions(surveyBlockList, answerList),
            ),
            setSectionHeaders({}),
            setAnswerList(answerList),
            setResponseUuid(responseUuid),
            setSharedHeader(headerUuid),
            setVisitedPages(visitedPages),
            setVisitedQuestions(visitedQuestions),
          ]),
        );
        map(headers, (header) => dispatch(applySectionHeaders(get(header, 'sectionAssignee'), header)));
        dispatch(setInitialPage(lastClickedNextButtonId));
      },
    );
